import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, MapPin, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import SmartImage from '../components/SmartImage';

const Delegates = () => {
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadDelegates();
  }, []);

  const loadDelegates = async () => {
    try {
      const res = await api.get('/delegates/');
      setDelegates(res.data);
    } catch (error) {
      console.error('Error loading delegates:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % delegates.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + delegates.length) % delegates.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <p>Cargando delegados...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="delegates-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b1220] via-[#1a2744] to-[#0b1220] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#c5a465]/20 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-[#c5a465]" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Credenciales de Delegados
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Representantes oficiales de IAASP Academy en diferentes países de Latinoamérica y el mundo.
          </p>
        </div>
      </section>

      {/* Delegates Carousel */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {delegates.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">Sin delegados registrados</h3>
              <p className="text-muted-foreground">
                Próximamente se mostrarán las credenciales de nuestros delegados oficiales.
              </p>
            </Card>
          ) : (
            <>
              {/* Main Carousel */}
              <div className="relative">
                {/* Navigation Buttons */}
                {delegates.length > 1 && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg"
                      onClick={prevSlide}
                      data-testid="prev-delegate-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg"
                      onClick={nextSlide}
                      data-testid="next-delegate-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </>
                )}

                {/* Delegate Card */}
                <Card className="overflow-hidden bg-white shadow-xl">
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="md:w-1/2 bg-gradient-to-br from-[#0b1220] to-[#1a2744] p-8 flex items-center justify-center">
                      <SmartImage
                        src={delegates[currentIndex]?.image_url}
                        alt={delegates[currentIndex]?.name}
                        fallbackLabel={delegates[currentIndex]?.name || 'Delegado'}
                        className="max-w-full max-h-[500px] object-contain rounded-lg shadow-2xl"
                        data-testid="delegate-image"
                      />
                    </div>

                    {/* Info */}
                    <div className="md:w-1/2 p-8 flex flex-col justify-center">
                      <Badge className="w-fit mb-4 bg-[#c5a465] text-white">
                        Delegado Oficial
                      </Badge>
                      <h2 className="text-3xl font-heading font-bold text-primary mb-2" data-testid="delegate-name">
                        {delegates[currentIndex]?.name}
                      </h2>
                      <div className="flex items-center gap-2 text-muted-foreground mb-4">
                        <MapPin className="w-4 h-4" />
                        <span className="text-lg" data-testid="delegate-country">{delegates[currentIndex]?.country}</span>
                      </div>
                      {delegates[currentIndex]?.position && (
                        <p className="text-lg text-muted-foreground">
                          {delegates[currentIndex].position}
                        </p>
                      )}
                      
                      <div className="mt-8 pt-6 border-t">
                        <p className="text-sm text-muted-foreground">
                          Credencial verificada por IAASP - International Alliance of Advanced Security Programs
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Dots Navigation */}
                {delegates.length > 1 && (
                  <div className="flex justify-center gap-2 mt-6">
                    {delegates.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentIndex 
                            ? 'bg-[#c5a465] w-8' 
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        data-testid={`delegate-dot-${index}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Grid View (for mobile alternative) */}
              <div className="mt-12">
                <h3 className="text-xl font-semibold text-center mb-6">Todos los Delegados</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {delegates.map((delegate, index) => (
                    <button
                      key={delegate.id}
                      onClick={() => goToSlide(index)}
                      className={`p-2 rounded-lg transition-all ${
                        index === currentIndex 
                          ? 'ring-2 ring-[#c5a465] bg-white shadow-md' 
                          : 'bg-white hover:shadow-md'
                      }`}
                    >
                      <SmartImage
                        src={delegate.image_url}
                        alt={delegate.name}
                        fallbackLabel={delegate.name}
                        className="w-full h-24 object-cover rounded"
                      />
                      <p className="text-xs mt-2 font-medium truncate">{delegate.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{delegate.country}</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl font-heading font-bold text-primary mb-4">
            ¿Quieres ser Delegado de IAASP?
          </h2>
          <p className="text-muted-foreground mb-6">
            Si estás interesado en representar a IAASP Academy en tu país, contáctanos para más información.
          </p>
          <Button className="bg-[#c5a465] hover:bg-[#b8964f] text-white">
            Contactar
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Delegates;
